import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import type { HotkeyConfig } from "capitalrx-hotkeys";

import { DISPLAY_NAMES, isDevelopmentMode } from "../constants";
import { HotkeyErrorBoundary } from "./HotkeyErrorBoundary";
import { useHotkeyContext } from "./HotkeyProvider";

interface DebugOverlayProps {
  /** Position of the overlay on screen */
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  /** Initial visibility state */
  defaultVisible?: boolean;
  /** Keyboard shortcut to toggle visibility (default: CTRL+SHIFT+H) */
  toggleShortcut?: string;
}

interface HotkeyDebugInfo {
  id: string;
  combo: string;
  description: string;
  scope: string;
  priority: number;
  enabled: boolean;
}

const POSITION_STYLES: Record<string, React.CSSProperties> = {
  "top-left": { top: 16, left: 16 },
  "top-right": { top: 16, right: 16 },
  "bottom-left": { bottom: 16, left: 16 },
  "bottom-right": { bottom: 16, right: 16 },
};

const OVERLAY_STYLES: React.CSSProperties = {
  position: "fixed",
  /* CSS custom property reference is intentional — React.CSSProperties.zIndex accepts string */
  zIndex: "var(--z-index-toast, 1600)",
  maxWidth: 420,
  maxHeight: "60vh",
  overflow: "auto",
  backgroundColor: "rgba(0, 0, 0, 0.9)",
  color: "#fff",
  fontFamily: "monospace",
  fontSize: 11,
  borderRadius: 8,
  padding: 12,
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.4)",
};

const HEADER_STYLES: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 12,
  paddingBottom: 8,
  borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
};

const TABLE_STYLES: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 10,
};

const TH_STYLES: React.CSSProperties = {
  textAlign: "left",
  padding: "4px 8px",
  borderBottom: "1px solid rgba(255, 255, 255, 0.3)",
  fontWeight: 600,
  color: "#aaa",
};

const TD_STYLES: React.CSSProperties = {
  padding: "4px 8px",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  verticalAlign: "top",
};

const BADGE_STYLES: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 6px",
  backgroundColor: "rgba(255, 255, 255, 0.15)",
  borderRadius: 4,
  fontSize: 10,
  fontWeight: 500,
};

const SCOPE_BADGE_STYLES: React.CSSProperties = {
  ...BADGE_STYLES,
  backgroundColor: "rgba(100, 200, 255, 0.25)",
  color: "#8cf",
};

const DISABLED_STYLES: React.CSSProperties = {
  opacity: 0.4,
  textDecoration: "line-through",
};

/**
 * Debug overlay component for visualizing registered hotkeys.
 * Only renders in development mode (process.env.NODE_ENV === "development").
 * Toggle visibility with CTRL+SHIFT+H by default.
 */
export function DebugOverlay({
  position = "bottom-right",
  defaultVisible = false,
  toggleShortcut = "ctrl+shift+h",
}: DebugOverlayProps): ReactNode {
  const [isVisible, setIsVisible] = useState(defaultVisible);
  const { manager, enabled: hotkeysEnabled } = useHotkeyContext();
  const isDevelopment = isDevelopmentMode();

  // Toggle visibility with keyboard shortcut
  useEffect(() => {
    // Only set up listeners in development mode
    if (!isDevelopment) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      const keys = toggleShortcut.toLowerCase().split("+");
      const needsCtrl = keys.includes("ctrl");
      const needsShift = keys.includes("shift");
      const needsAlt = keys.includes("alt");
      const needsMeta = keys.includes("meta");
      const targetKey = keys.find((k) => !["ctrl", "shift", "alt", "meta"].includes(k));

      if (
        event.ctrlKey === needsCtrl
        && event.shiftKey === needsShift
        && event.altKey === needsAlt
        && event.metaKey === needsMeta
        && event.key.toLowerCase() === targetKey
      ) {
        event.preventDefault();
        setIsVisible((v) => !v);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleShortcut, isDevelopment]);

  // Only render in development mode
  if (!isDevelopment) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  // Gather hotkey information
  const hotkeys: HotkeyDebugInfo[] = [];
  const allHotkeys = manager.getHotkeys?.() || [];

  for (const entry of allHotkeys) {
    const config = entry.config as HotkeyConfig & { title?: string };
    const modifiers = config.modifiers || [];
    const combo = modifiers.length > 0 ? `${modifiers.join("+")}+${config.key}` : config.key;

    hotkeys.push({
      id: entry.id || combo,
      combo: combo.toUpperCase(),
      description: config.description || config.title || "Unnamed",
      scope: entry.scope || config.scope || "global",
      priority: config.priority ?? 50,
      enabled: entry.enabled !== false,
    });
  }

  // Sort by priority (highest first), then by scope
  hotkeys.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return a.scope.localeCompare(b.scope);
  });

  // Get scope stack info if available
  let scopeStack: string[] = [];
  let activeScope = "global";
  try {
    scopeStack = manager.scopeManager?.getScopeStack?.() || [];
    activeScope = manager.getActiveScope?.() || "global";
  } catch {
    // scopeManager may not be exposed
  }

  return (
    <HotkeyErrorBoundary>
      <div style={{ ...OVERLAY_STYLES, ...POSITION_STYLES[position] }} role="region" aria-label="Hotkeys debug panel" aria-live="polite">
        <div style={HEADER_STYLES}>
          <div>
            <strong style={{ fontSize: 13 }}>🔧 HotKey Debug</strong>
            <span style={{ marginLeft: 8, opacity: 0.6 }}>({toggleShortcut.toUpperCase()} to close)</span>
          </div>
          <button
            type="button"
            onClick={() => setIsVisible(false)}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: 16,
              padding: 4,
            }}
            aria-label="Close debug panel"
          >
            ✕
          </button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4 }}>
            <strong>Status:</strong>{" "}
            <span style={{ color: hotkeysEnabled ? "#8f8" : "#f88" }}>{hotkeysEnabled ? "Enabled" : "Disabled"}</span>
          </div>
          <div style={{ marginBottom: 4 }}>
            <strong>Active Scope:</strong> <span style={SCOPE_BADGE_STYLES}>{activeScope}</span>
          </div>
          {scopeStack.length > 0 && (
            <div>
              <strong>Scope Stack:</strong>{" "}
              {scopeStack.map((s) => (
                <span key={`scope-${s}`} style={{ ...SCOPE_BADGE_STYLES, marginLeft: 4 }}>
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 8 }}>
          <strong>Registered Hotkeys ({hotkeys.length})</strong>
        </div>

        <table style={TABLE_STYLES} role="table" aria-label="Registered hotkeys">
          <thead>
            <tr>
              <th style={TH_STYLES} scope="col">
                Shortcut
              </th>
              <th style={TH_STYLES} scope="col">
                Action
              </th>
              <th style={TH_STYLES} scope="col">
                Scope
              </th>
              <th style={TH_STYLES} scope="col">
                Pri
              </th>
            </tr>
          </thead>
          <tbody>
            {hotkeys.map((hotkey) => (
              <tr key={hotkey.id} style={!hotkey.enabled ? DISABLED_STYLES : undefined}>
                <td style={TD_STYLES}>
                  <span style={BADGE_STYLES}>{hotkey.combo}</span>
                </td>
                <td style={TD_STYLES}>{hotkey.description}</td>
                <td style={TD_STYLES}>
                  <span style={SCOPE_BADGE_STYLES}>{hotkey.scope}</span>
                </td>
                <td style={{ ...TD_STYLES, textAlign: "center" }}>{hotkey.priority}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {hotkeys.length === 0 && <div style={{ textAlign: "center", padding: 16, opacity: 0.6 }}>No hotkeys registered</div>}
      </div>
    </HotkeyErrorBoundary>
  );
}

DebugOverlay.displayName = DISPLAY_NAMES.DebugOverlay || "DebugOverlay";
